import {
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@radix-ui/react-dialog";
import { observer } from "mobx-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
} from "@/components/ui/dialog";
import { useRootStore } from "@/state/RootStore";
import { upsertProfile } from "@/api/fetch";
import { getCoachmarkSteps } from "@/i18n/coachmarkCopy";
import {
  findCoachmarkTarget,
  useCoachmarkTargetRect,
} from "@/hooks/useCoachmarkTargetRect";
import { getCoachmarkLayout } from "@/utils/coachmarkLayout";
import {
  APP_TO_BUILD,
  ElectricalUSPracticeTestType,
  IbewAptitudePracticeTestType,
  PlumbingPracticeTestType,
  PracticeTestType,
} from "../../../appsConfig";

interface CoachmarksModalProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

function getTourTypes() {
  if (APP_TO_BUILD === "electrical_us") {
    return [
      PracticeTestType.quickPracticeType,
      ElectricalUSPracticeTestType.BANNER_AI_CHAT,
      ElectricalUSPracticeTestType.CODEBOOK_MEMORIZATION,
    ];
  }
  if (APP_TO_BUILD === "plumbing") {
    return [PlumbingPracticeTestType.QUICK, PlumbingPracticeTestType.BANNER_AI_CHAT];
  }
  if (APP_TO_BUILD === "ibew_aptitude") {
    return [PracticeTestType.quickPracticeType, IbewAptitudePracticeTestType.BANNER_AI_CHAT];
  }
  return [PracticeTestType.quickPracticeType, PracticeTestType.aiChat];
}

const CoachmarksModal = observer(({
  onOpenChange,
  open,
}: CoachmarksModalProps) => {
  const store = useRootStore();
  const { t } = useTranslation();
  const [tourStep, setTourStep] = useState(0);
  const tourTypes = useMemo(getTourTypes, []);
  const steps = useMemo(() => getCoachmarkSteps(t, APP_TO_BUILD), [t]);
  const stepCount = Math.min(tourTypes.length, steps.length);
  const currentType = tourTypes[tourStep];
  const targetRect = useCoachmarkTargetRect(currentType, open);
  const calloutRef = useRef<HTMLDivElement>(null);
  const [calloutHeight, setCalloutHeight] = useState(240);
  const layout = useMemo(
    () => targetRect ? getCoachmarkLayout(targetRect, calloutHeight) : null,
    [calloutHeight, targetRect],
  );

  useEffect(() => {
    if (!open) return;
    void upsertProfile({ have_shown_coachmark: true });
    store.mainAppStore.showCoachmark = false;
  }, [open, store]);

  useEffect(() => {
    if (!open) setTourStep(0);
  }, [open]);

  useEffect(() => {
    if (!open || currentType == null) return;

    const target = findCoachmarkTarget(currentType);
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const gutter = 24;
    const isOutsideViewport =
      rect.top < gutter ||
      rect.bottom > window.innerHeight - gutter ||
      rect.left < gutter ||
      rect.right > window.innerWidth - gutter;

    if (isOutsideViewport) {
      target.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  }, [currentType, open]);

  useLayoutEffect(() => {
    const callout = calloutRef.current;
    if (!callout || !open) return;

    const measure = () => {
      setCalloutHeight(callout.scrollHeight);
    };
    const resizeObserver = new ResizeObserver(measure);

    measure();
    resizeObserver.observe(callout);

    return () => resizeObserver.disconnect();
  }, [open, tourStep, targetRect?.viewportWidth]);

  const advance = () => {
    if (tourStep >= stepCount - 1) {
      onOpenChange(false);
      return;
    }
    setTourStep((step) => step + 1);
  };

  if (currentType == null || stepCount === 0) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="!bg-transparent" />
        {targetRect && (
          <div
            aria-hidden="true"
            className="pointer-events-none fixed z-[55]"
            style={{
              borderRadius: targetRect.borderRadius,
              boxShadow: "0 0 0 9999px rgba(10, 12, 17, 0.75), 0 16px 40px rgba(0, 0, 0, 0.22)",
              height: targetRect.height,
              left: targetRect.left,
              outline: "2px solid rgba(255, 255, 255, 0.92)",
              top: targetRect.top,
              width: targetRect.width,
            }}
          />
        )}
        <DialogContent
          aria-describedby="coachmark-description"
          className="pointer-events-none fixed inset-0 z-[60] outline-none"
        >
          <DialogTitle className="sr-only">{steps[tourStep].title}</DialogTitle>
          {layout && (
            <div
              className="pointer-events-auto fixed"
              style={{
                left: layout.calloutLeft,
                top: layout.calloutTop,
                width: layout.calloutWidth,
              }}
            >
              <span
                aria-hidden="true"
                className={`absolute z-10 h-5 w-5 rotate-45 rounded-sm bg-white ${layout.placement === "below" ? "-top-2" : "-bottom-2"}`}
                style={{ left: layout.arrowLeft }}
              />
              <div
                className="relative overflow-y-auto rounded-[24px] bg-white p-5 shadow-[0_16px_40px_rgba(0,0,0,0.25)]"
                ref={calloutRef}
                style={{ maxHeight: layout.calloutMaxHeight }}
              >
                <h2 className="text-xl font-semibold leading-7 text-[#0a0c11] sm:text-2xl sm:leading-8">
                  {steps[tourStep].title}
                </h2>
                <DialogDescription id="coachmark-description" className="mt-2 text-sm leading-5 text-[#5b616d] sm:text-base sm:leading-6">
                  {steps[tourStep].body}
                </DialogDescription>
                <div className="mt-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-1">
                    {Array.from({ length: stepCount }, (_, index) => (
                      <span className={`h-1 rounded-full ${index === tourStep ? "w-[18px] bg-[var(--dp-brand-button)]" : "w-1 bg-[#dddfe4]"}`} key={index} />
                    ))}
                  </div>
                  <button className="dp-button-secondary h-11 shrink-0 px-5" onClick={advance} type="button">
                    {t("common.next")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
});

export default CoachmarksModal;
